import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { http, money } from "@/lib/api";

const FILTERS = [
  { k: "all", label: "All" },
  { k: "messages", label: "With messages" },
  { k: "monthly", label: "Monthly" },
  { k: "sponsors", label: "Sponsors" },
];
const TINTS = ["bg-aqua", "bg-quartz", "bg-mustard", "bg-lav", "bg-tang"];

export const DonorWall = ({ limit, showControls = true }) => {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  useEffect(() => {
    http.get("/donor-wall").then((r) => setRows(r.data)).catch(() => setRows([]));
  }, []);

  const list = useMemo(() => {
    let l = rows || [];
    if (filter === "messages") l = l.filter((r) => r.message);
    if (filter === "monthly") l = l.filter((r) => r.recurring);
    if (filter === "sponsors") l = l.filter((r) => r.kind === "sponsorship");
    if (q) l = l.filter((r) => `${r.name} ${r.organization} ${r.message}`.toLowerCase().includes(q.toLowerCase()));
    return limit ? l.slice(0, limit) : l;
  }, [rows, filter, q, limit]);

  return (
    <div data-testid="donor-wall">
      {showControls && (
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between mb-8">
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button key={f.k} onClick={() => setFilter(f.k)} data-testid={`donor-wall-filter-${f.k}`}
                className={`px-4 py-1.5 rounded-full text-xs font-mono uppercase tracking-wider border transition-colors ${filter === f.k ? "bg-ink border-ink text-shell" : "border-ink/30 hover:border-ink"}`}>{f.label}</button>
            ))}
          </div>
          <div className="relative sm:w-64">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-mas-mute" />
            <input className="field !pl-9" placeholder="Search names" value={q} onChange={(e) => setQ(e.target.value)} data-testid="donor-wall-search-input" />
          </div>
        </div>
      )}
      {rows === null ? (
        <div className="h-32 animate-pulse paper-card" />
      ) : list.length === 0 ? (
        <div className="border-2 border-dashed border-mas-sand p-10 text-center" data-testid="donor-wall-empty">
          <p className="text-2xl font-semibold">The wall is waiting for its first verse.</p>
          <p className="text-mas-mute mt-2 text-sm">Be among the first names here — every gift keeps the mic free.</p>
        </div>
      ) : (
        <div className="columns-1 sm:columns-2 lg:columns-3 masonry">
          {list.map((r, i) => (
            <article key={r.id} className={`${TINTS[i % TINTS.length]} text-ink p-5 ${i % 3 === 1 ? "rotate-[0.6deg]" : i % 3 === 2 ? "-rotate-[0.5deg]" : ""}`} data-testid={`donor-card-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-display text-xl leading-tight">{r.name}</p>
                  {r.organization && <p className="text-xs text-mas-mute mt-0.5">{r.organization}</p>}
                </div>
                {r.amount != null && <span className="font-mono text-sm font-bold whitespace-nowrap">{money(r.amount)}</span>}
              </div>
              {r.message && <p className="mt-3 text-sm leading-relaxed opacity-80">“{r.message}”</p>}
              <div className="mt-3 flex gap-2">
                {r.recurring && <span className="font-mono text-[10px] uppercase tracking-wider bg-ink text-shell px-2 py-0.5">Monthly</span>}
                {r.kind === "sponsorship" && <span className="font-mono text-[10px] uppercase tracking-wider bg-shell text-ink px-2 py-0.5">Sponsor</span>}
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};
